/**
 * La grille de la semaine, lue depuis l'INTERVENTION UNIQUE (lot U1c, ADR-0016) : un technicien par ligne, les jours en colonnes,
 * UNE CASE PAR JOUR TRAVAILLÉ (un bloc = un jour d'une intervention pour un membre de son équipe, le chef marqué).
 *
 * La première ligne est « À affecter » : les jours travaillés qui n'ont pas encore d'équipe, à leur date. Les interventions sans
 * aucun jour attendent dans la liste de droite.
 *
 * GLISSER : on prend un bloc et on le dépose sur une case — un jour, et une ligne : celle d'un technicien (il remplace celui d'où
 * on prend le bloc ; les autres membres restent) ou « À affecter » (le technicien part). La grille ne décide de rien : elle
 * signale ce qu'on a pris et où on le dépose, la page fait les écritures (`etapesDeDeplacement`). Un bloc est un
 * `div role="button"`, jamais un `<button>` : Firefox refuse de démarrer un glisser sur un bouton, et la règle de survol générale
 * des boutons rendrait le texte illisible. Seule une intervention ouverte se glisse ; la ligne d'un technicien archivé ne reçoit rien.
 *
 * MATIN / APRÈS-MIDI (lot T0, 2026-10-25) : pendant un glisser, chaque case se partage en deux zones de dépôt, « Matin » en haut et
 * « Après-midi » en bas ; déposer pose aussi le créneau. Les blocs arrivent du serveur déjà rangés (matin, dans la journée,
 * après-midi, heure de rendez-vous, rang) ; le bloc sélectionné porte deux flèches pour monter ou descendre dans la journée.
 *
 * Les classes sont celles de la grille des visites : même rendu. Tout ce qui est montré vient du serveur (zone, retard, urgence).
 */
import { useEffect, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';

import type { CaseSemaine, CreneauJour, SemaineInterventions } from '../api/client';
import { LIBELLE_ABSENCE, absenceDuJour, niveauCharge, phraseCharge, precisionsCharge } from '../domaine/planning-absences';
import { dureeFr, estWeekend, initiales, jourCourt, jourFr } from '../domaine/planning';
import { couleurEtat } from '../domaine/planning-interventions';
import { enteteJourMois, estVueMois } from '../domaine/planning-calendrier';
import {
  casesDeLaLigne,
  infobulleBloc,
  lignesDuBloc,
  LIBELLE_CRENEAU,
  numeroAffiche,
  numeroCourt,
  peutDeplacer,
  placeDuBloc,
  tempsPrevuDuJour,
  type CibleCase,
} from '../domaine/planning-interventions';

/** Ce que la grille signale à la page du glisser : le bloc pris, la fin du geste, l'endroit du dépôt. */
export interface GestesGlisserIntervention {
  /** Un glisser est en cours (un bloc de la grille ou une ligne de la liste) : les cases qui peuvent le recevoir s'éclairent. */
  readonly enCours: boolean;
  readonly onGlisser: (c: CaseSemaine) => void;
  /** Le geste est fini (déposé ou lâché ailleurs) : tout se remet à zéro. */
  readonly onFinGlisser: () => void;
  readonly onDeposer: (cible: CibleCase) => void;
}

/** Monter (-1) ou descendre (+1) une intervention dans la case d'un technicien (ou de « À affecter ») un jour. */
export type Ranger = (cases: readonly CaseSemaine[], interventionId: string, sens: -1 | 1) => void;

export function PlanningInterventionsGrille({
  semaine,
  selection,
  peutGerer,
  glisser,
  onRanger,
  onSelectionner,
  onNouvelle,
}: {
  semaine: SemaineInterventions;
  selection: string | null;
  peutGerer: boolean;
  glisser: GestesGlisserIntervention;
  onRanger: Ranger;
  onSelectionner: (interventionId: string) => void;
  /** Ouvre le volet de création pour ce jour et ce technicien (`null` = « À affecter »). */
  onNouvelle: (jour: string, technicienId: string | null) => void;
}) {
  // Vue « Mois » (2026-10-30) : une colonne étroite par jour, des blocs réduits au numéro et au client ; la grille défile en largeur.
  const mois = estVueMois(semaine);
  const colonnes = mois
    ? `minmax(150px, 170px) ${semaine.jours.map((j) => (estWeekend(j) ? 'minmax(30px, 0.5fr)' : 'minmax(50px, 1fr)')).join(' ')}`
    : `minmax(150px, 190px) ${semaine.jours.map((j) => (estWeekend(j) ? 'minmax(78px, 0.5fr)' : 'minmax(0, 1fr)')).join(' ')}`;

  return (
    <div
      className={`planning-grille${mois ? ' mois' : ''}`}
      style={{ gridTemplateColumns: colonnes }}
      role="table"
      aria-label={mois ? 'Planning du mois' : 'Planning de la semaine'}
    >
      <div className="planning-coin" role="columnheader" />
      {semaine.jours.map((j) => (
        <div
          key={j}
          role="columnheader"
          className={`planning-jour${j === semaine.today ? ' aujourdhui' : ''}${estWeekend(j) ? ' weekend' : ''}`}
          title={jourFr(j)}
        >
          {mois ? <EnteteJourMois jour={j} /> : jourCourt(j)}
        </div>
      ))}

      <div className="planning-nom a-affecter" role="rowheader">
        <strong>À affecter</strong>
        <span className="aide">jour fixé, équipe à choisir</span>
      </div>
      {semaine.jours.map((j) => (
        <Case
          key={`aff-${j}`}
          jour={j}
          aujourdhui={semaine.today}
          technicienId={null}
          ligneAAffecter
          libelleAjout={`Nouvelle intervention le ${jourFr(j)}, équipe à choisir`}
          cases={casesDeLaLigne(semaine.cases, null, j)}
          compact={mois}
          selection={selection}
          peutGerer={peutGerer}
          accepteDepot={peutGerer}
          glisser={glisser}
          onRanger={onRanger}
          onSelectionner={onSelectionner}
          onNouvelle={() => onNouvelle(j, null)}
        />
      ))}

      {semaine.technicians.length === 0 && (
        <div className="planning-vide">Aucun technicien. Créez-en depuis le planning des visites : ils apparaîtront ici, une ligne chacun.</div>
      )}

      {semaine.technicians.map((t) => (
        <Ligne key={t.id} technicien={t} semaine={semaine} selection={selection} peutGerer={peutGerer} glisser={glisser} onRanger={onRanger} onSelectionner={onSelectionner} onNouvelle={onNouvelle} />
      ))}
    </div>
  );
}

function Ligne({
  technicien,
  semaine,
  selection,
  peutGerer,
  glisser,
  onRanger,
  onSelectionner,
  onNouvelle,
}: {
  technicien: SemaineInterventions['technicians'][number];
  semaine: SemaineInterventions;
  selection: string | null;
  peutGerer: boolean;
  glisser: GestesGlisserIntervention;
  onRanger: Ranger;
  onSelectionner: (id: string) => void;
  onNouvelle: (jour: string, technicienId: string | null) => void;
}) {
  return (
    <>
      <div className={`planning-nom${technicien.archived ? ' archive' : ''}`} role="rowheader">
        <span className="planning-initiales" aria-hidden="true">
          {initiales(technicien.name)}
        </span>
        <span>
          <strong>{technicien.name}</strong>
          {technicien.archived && <span className="aide"> archivé : ses interventions ouvertes restent à reprendre</span>}
          <span
            className={`planning-charge ${niveauCharge(technicien.charge)}`}
            title={`Charge PRÉVUE ${estVueMois(semaine) ? 'du mois' : 'de la semaine'} sur la capacité (${semaine.dayMinutes / 60} h par jour ouvré, hors jours d'absence)`}
          >
            {phraseCharge(technicien.charge)}
          </span>
          {precisionsCharge(technicien.charge).length > 0 && <span className="aide planning-charge-precision">{precisionsCharge(technicien.charge).join(' · ')}</span>}
        </span>
      </div>
      {semaine.jours.map((j) => {
        const cases = casesDeLaLigne(semaine.cases, technicien.id, j);
        const temps = tempsPrevuDuJour(semaine.cases, technicien.id, j);
        return (
          <Case
            key={`${technicien.id}-${j}`}
            jour={j}
            aujourdhui={semaine.today}
            technicienId={technicien.id}
            libelleAjout={`Nouvelle intervention le ${jourFr(j)} pour ${technicien.name}`}
            cases={cases}
            compact={estVueMois(semaine)}
            selection={selection}
            // On ne confie rien à un technicien archivé : sa ligne ne reçoit rien.
            peutGerer={peutGerer && !technicien.archived}
            accepteDepot={peutGerer && !technicien.archived}
            glisser={glisser}
            onRanger={onRanger}
            absence={absenceDuJour(semaine.absences, technicien.id, j)}
            // En vue Mois la colonne est trop étroite pour le temps du jour : la charge du mois reste sur le nom du technicien.
            temps={cases.length > 0 && !estVueMois(semaine) ? temps : null}
            onSelectionner={onSelectionner}
            onNouvelle={() => onNouvelle(j, technicien.id)}
          />
        );
      })}
    </>
  );
}

function Case({
  jour,
  aujourdhui,
  technicienId,
  cases,
  compact = false,
  ligneAAffecter = false,
  libelleAjout,
  selection,
  peutGerer,
  accepteDepot,
  glisser,
  onRanger,
  absence = null,
  temps = null,
  onSelectionner,
  onNouvelle,
}: {
  jour: string;
  aujourdhui: string;
  /** La ligne de la case : un technicien, ou `null` pour « À affecter ». */
  technicienId: string | null;
  cases: readonly CaseSemaine[];
  /** Vue Mois : blocs réduits au numéro et au client. */
  compact?: boolean;
  ligneAAffecter?: boolean;
  libelleAjout: string;
  selection: string | null;
  peutGerer: boolean;
  /** Cette case peut-elle recevoir un bloc qu'on glisse ? */
  accepteDepot: boolean;
  glisser: GestesGlisserIntervention;
  onRanger: Ranger;
  absence?: ReturnType<typeof absenceDuJour>;
  temps?: { minutes: number; sansDuree: number } | null;
  onSelectionner: (id: string) => void;
  onNouvelle: () => void;
}) {
  const [survol, setSurvol] = useState(false);
  /** La demi-journée sous le pointeur pendant un glisser (un état, pas une règle :hover : le survol ne joue pas pendant un glisser). */
  const [demi, setDemi] = useState<CreneauJour | null>(null);
  const cible = accepteDepot && glisser.enCours;
  // Le geste est fini : la case ne garde pas son éclairage pour le glisser suivant.
  useEffect(() => {
    if (!cible) {
      setSurvol(false);
      setDemi(null);
    }
  }, [cible]);

  const survolee = (e: DragEvent<HTMLDivElement>) => {
    if (!cible) return;
    // Sans `preventDefault`, le navigateur refuse le dépôt.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const quitte = (e: DragEvent<HTMLDivElement>) => {
    // Passer sur un bloc de la case déclenche un « leave » de la case : on ne s'éteint que si on sort VRAIMENT d'elle.
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSurvol(false);
  };
  const depose = (e: DragEvent<HTMLDivElement>) => {
    if (!cible) return;
    e.preventDefault();
    setSurvol(false);
    glisser.onDeposer({ jour, technicianId: technicienId });
  };
  // Une demi-journée : le dépôt pose aussi le créneau. L'événement ne remonte pas à la case (qui déposerait une seconde fois).
  const deposeDemi = (slot: CreneauJour) => (e: DragEvent<HTMLDivElement>) => {
    if (!cible) return;
    e.preventDefault();
    e.stopPropagation();
    setSurvol(false);
    setDemi(null);
    glisser.onDeposer({ jour, technicianId: technicienId, slot });
  };

  return (
    <div
      role="cell"
      className={`planning-case${estWeekend(jour) ? ' weekend' : ''}${jour === aujourdhui ? ' aujourdhui' : ''}${ligneAAffecter ? ' a-affecter' : ''}${absence ? ' absent' : ''}${cible ? ' depot-possible' : ''}${cible && survol ? ' depot-survol' : ''}`}
      onDragEnter={cible ? () => setSurvol(true) : undefined}
      onDragOver={survolee}
      onDragLeave={cible ? quitte : undefined}
      onDrop={depose}
    >
      {absence && (
        <div className={`planning-absence ${absence.kind.toLowerCase()}`} title={LIBELLE_ABSENCE[absence.kind]}>
          {LIBELLE_ABSENCE[absence.kind]}
        </div>
      )}
      {cible && (
        <div className="planning-demis" aria-hidden="true">
          {(['MORNING', 'AFTERNOON'] as const).map((slot) => (
            <div
              key={slot}
              className={`planning-demi${demi === slot ? ' survol' : ''}`}
              onDragEnter={() => setDemi(slot)}
              onDragOver={survolee}
              onDrop={deposeDemi(slot)}
            >
              {LIBELLE_CRENEAU[slot]}
            </div>
          ))}
        </div>
      )}
      {cases.map((c) => {
        const actif = c.intervention.id === selection;
        return (
          <div key={`${c.intervention.id}-${c.technicianId ?? ''}`}>
            <Bloc caseSemaine={c} actif={actif} compact={compact} glissable={peutDeplacer(c.intervention, peutGerer)} glisser={glisser} onSelectionner={onSelectionner} />
            {actif && peutDeplacer(c.intervention, peutGerer) && cases.length > 1 && (
              <div className="planning-ranger">
                <button type="button" className="secondaire discret" title="Plus tôt dans la journée" aria-label="Monter dans la journée" onClick={() => onRanger(cases, c.intervention.id, -1)}>
                  ↑
                </button>
                <button type="button" className="secondaire discret" title="Plus tard dans la journée" aria-label="Descendre dans la journée" onClick={() => onRanger(cases, c.intervention.id, 1)}>
                  ↓
                </button>
              </div>
            )}
          </div>
        );
      })}
      {temps && (temps.minutes > 0 || temps.sansDuree > 0) && (
        <div className="planning-temps" title="Temps prévu ce jour-là : la somme des durées estimées">
          {temps.minutes > 0 ? dureeFr(temps.minutes) : ''}
          {temps.sansDuree > 0 ? `${temps.minutes > 0 ? ' + ' : ''}${temps.sansDuree} sans durée` : ''}
        </div>
      )}
      {peutGerer && (
        <button type="button" className="secondaire discret planning-ajout" aria-label={libelleAjout} title={libelleAjout} onClick={onNouvelle}>
          +
        </button>
      )}
    </div>
  );
}

function Bloc({
  caseSemaine: c,
  actif,
  compact,
  glissable,
  glisser,
  onSelectionner,
}: {
  caseSemaine: CaseSemaine;
  actif: boolean;
  compact: boolean;
  glissable: boolean;
  glisser: GestesGlisserIntervention;
  onSelectionner: (id: string) => void;
}) {
  const [tenu, setTenu] = useState(false);
  const i = c.intervention;
  const lignes = lignesDuBloc(c);
  const place = placeDuBloc(c);
  const classes = [
    'planning-bloc',
    compact ? 'compact' : '',
    `etat-${couleurEtat(i.status).classe}`,
    actif ? 'actif' : '',
    glissable ? 'glissable' : '',
    tenu ? 'tenu' : '',
    i.status === 'CANCELLED' ? 'annulee' : '',
    c.enRetard ? 'retard' : '',
    i.tentative ? 'tentative' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const surTouche = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelectionner(i.id);
    }
  };

  // Le bloc qu'on tient s'estompe à sa place APRÈS le début du geste : le navigateur photographie le bloc au retour de `dragstart`.
  const debut = (e: DragEvent<HTMLDivElement>) => {
    // Firefox ne démarre pas un glisser sans donnée ; le bloc lui-même est gardé dans l'état de la page.
    e.dataTransfer.setData('text/plain', i.id);
    e.dataTransfer.effectAllowed = 'move';
    glisser.onGlisser(c);
    window.setTimeout(() => setTenu(true), 0);
  };
  const fin = () => {
    setTenu(false);
    glisser.onFinGlisser();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={classes}
      aria-pressed={actif}
      aria-label={`${lignes.titre}, ${lignes.duree}`}
      title={infobulleBloc(c)}
      draggable={glissable || undefined}
      onClick={() => onSelectionner(i.id)}
      onKeyDown={surTouche}
      onDragStart={glissable ? debut : undefined}
      onDragEnd={glissable ? fin : undefined}
    >
      {compact && (
        // Vue Mois : le numéro et le client seulement ; tout le reste est dans la bulle et dans le détail à droite.
        <>
          <span className={`planning-etiquette ${i.number === null ? 'tournee' : 'numero'}`}>{i.number === null ? numeroAffiche(i) : numeroCourt(i.number)}</span>
          <span className="planning-bloc-client">{lignes.titre}</span>
        </>
      )}
      {!compact && place !== '' && <div className="planning-bloc-place">{place}</div>}
      {!compact && (
      <>
      <div className="planning-bloc-haut">
        <span>{lignes.duree}</span>
        {c.isLead && <span className="planning-etiquette equipe">chef</span>}
        {i.number === null ? (
          <span className="planning-etiquette tournee">{numeroAffiche(i)}</span>
        ) : (
          <span className="planning-etiquette numero">{numeroCourt(i.number)}</span>
        )}
        {i.creeeSurLeTerrain && (
          <span className="planning-etiquette terrain" title="Créée par le technicien sur son téléphone">
            terrain
          </span>
        )}
      </div>
      <strong className="planning-bloc-client">{lignes.titre}</strong>
      {lignes.lieu && <span className="planning-bloc-lieu">{lignes.lieu}</span>}
      <span className="planning-bloc-machines">{lignes.machine}</span>
      {i.natureLabel && <span className="planning-bloc-nature">{i.natureLabel}</span>}
      </>
      )}
    </div>
  );
}

/** L'en-tête d'une colonne en vue Mois : l'initiale du jour au-dessus de son numéro (« L » / « 26 »). */
function EnteteJourMois({ jour }: { jour: string }) {
  const { initiale, numero } = enteteJourMois(jour);
  return (
    <span className="planning-jour-mois">
      <span>{initiale}</span>
      <strong>{numero}</strong>
    </span>
  );
}
